import { useState } from 'react';
import { Select } from '@/components/ui/form';
import { Card, PageHeader, Pagination, SearchInput } from '@/components/ui/layout';
import { DataTable } from '@/components/ui/table';
import { formatDateTime } from '@/lib/format';
import { t } from '@/lib/i18n';
import { useList } from '@/lib/resources';
import type { AuditEntry } from '@/lib/types';

/** Entity types (sent to the API as the filter) with their labels. */
const entityLabels: Record<string, string> = {
  station: t('Station'),
  line: t('Direction'),
  zone: t('Line'),
  bus: t('Bus'),
  schedule: t('Schedule'),
  out_of_service_area: t('Out-of-service area'),
  interest_point: t('Interest point'),
  news: t('News'),
  report: t('Report'),
  notification: t('Notification'),
  campaign: t('Campaign'),
  advertisement: t('Advertisement'),
  pin_advertisement: t('Pin advertisement'),
  user: t('User'),
  file: t('File'),
};
const entities = Object.keys(entityLabels);

const actionLabels: Record<string, string> = {
  create: t('create'),
  update: t('update'),
  delete: t('delete'),
  login: t('sign in'),
  change_password: t('change password'),
  reply: t('reply'),
  archive: t('archive'),
  restore: t('restore'),
  upload: t('upload'),
  assign_zone: t('assign line'),
};

const label = (labels: Record<string, string>, value: string) => labels[value] ?? value.replace(/_/g, ' ');

export function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [entity, setEntity] = useState('');
  const [q, setQ] = useState('');
  const list = useList<AuditEntry>('audit-log', { page, pageSize: 50, entity, q });
  return (
    <>
      <PageHeader title={t('Audit log')} subtitle={t('Every change made in the admin panel.')} />
      <Card padded={false}>
        <div className="flex gap-2 border-b border-slate-200 px-4 py-3">
          <SearchInput
            value={q}
            onChange={(v) => {
              setQ(v);
              setPage(1);
            }}
            placeholder={t('Search user or item…')}
          />
          <Select
            className="w-56"
            value={entity}
            onChange={(e) => {
              setEntity(e.target.value);
              setPage(1);
            }}
          >
            <option value="">{t('Everything')}</option>
            {entities.map((e) => (
              <option key={e} value={e}>
                {label(entityLabels, e)}
              </option>
            ))}
          </Select>
        </div>
        <DataTable
          rows={list.data?.items}
          loading={list.isFetching}
          columns={[
            {
              header: t('When'),
              cell: (a) => <span className="whitespace-nowrap">{formatDateTime(a.at)}</span>,
            },
            { header: t('Who'), cell: (a) => a.user_email ?? '—' },
            { header: t('Action'), cell: (a) => label(actionLabels, a.action) },
            {
              header: t('Item'),
              cell: (a) => `${label(entityLabels, a.entity)}${a.summary ? `: ${a.summary}` : ''}`,
            },
            {
              header: t('Changed'),
              cell: (a) =>
                Array.isArray(a.changes) ? (
                  <span className="text-xs text-slate-500">{(a.changes as string[]).join(', ')}</span>
                ) : (
                  ''
                ),
            },
          ]}
        />
        {list.data && list.data.total > 0 && (
          <Pagination page={page} pageSize={50} total={list.data.total} onPage={setPage} />
        )}
      </Card>
    </>
  );
}
